/**
 * API client — thin fetch wrapper for all server communication.
 * Replaces localStorage-based saveState()/loadState().
 */

const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // Send session cookie
  };
  if (body) opts.body = JSON.stringify(body);

  const res = await fetch(path.startsWith('/auth') ? path : `${API_BASE}${path}`, opts);

  if (res.status === 204) return null as T;

  // Session expired — redirect to login
  if (res.status === 401 && !path.startsWith('/auth')) {
    const returnTo = window.location.pathname + window.location.search + window.location.hash;
    window.location.href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
    throw new ApiError(401, 'Session expired');
  }

  const data = await res.json();

  if (!res.ok) {
    throw new ApiError(res.status, data?.error?.message || 'Request failed', data);
  }

  return data as T;
}

// ─── Auth ──────────────────────────────────────────────

export const authService = {
  status: () => request<{ okta_configured: boolean; setup_required: boolean }>('GET', '/auth/status'),
  setup: (config: { issuer: string; client_id: string; client_secret: string; redirect_uri: string }) =>
    request<{ success: boolean; message: string }>('POST', '/auth/setup', config),
  me: () => request<{ user: any }>('GET', '/auth/me'),
  logout: () => request<{ success: boolean }>('POST', '/auth/logout'),
  loginUrl: '/auth/login',
};

// ─── Folders ───────────────────────────────────────────

export const folderService = {
  list: () => request<{ folders: any[] }>('GET', '/folders'),
  get: (id: string) => request<{ folder: any }>('GET', `/folders/${id}`),
  create: (data: { name: string; ref_id?: string }) => request<{ folder: any }>('POST', '/folders', data),
  update: (id: string, data: Partial<{ name: string; ref_id: string; settings: any }>) =>
    request<{ folder: any }>('PATCH', `/folders/${id}`, data),
  delete: (id: string) => request<void>('DELETE', `/folders/${id}`),

  // Members
  listMembers: (folderId: string) => request<{ members: any[] }>('GET', `/folders/${folderId}/members`),
  addMember: (folderId: string, email: string, role: 'editor' | 'viewer') =>
    request<{ message: string }>('POST', `/folders/${folderId}/members`, { email, role }),
  updateMemberRole: (folderId: string, userId: string, role: 'editor' | 'viewer') =>
    request<{ message: string }>('PATCH', `/folders/${folderId}/members/${userId}`, { role }),
  removeMember: (folderId: string, userId: string) =>
    request<void>('DELETE', `/folders/${folderId}/members/${userId}`),
};

// ─── Worksheets ────────────────────────────────────────

export const worksheetService = {
  list: (folderId: string) =>
    request<{ worksheets: any[] }>('GET', `/folders/${folderId}/worksheets`),
  get: (folderId: string, id: string) =>
    request<{ worksheet: any }>('GET', `/folders/${folderId}/worksheets/${id}`),
  create: (folderId: string, data: { name?: string; template_id?: string }) =>
    request<{ worksheet: any }>('POST', `/folders/${folderId}/worksheets`, data),
  update: (folderId: string, id: string, data: any) =>
    request<{ worksheet: any }>('PUT', `/folders/${folderId}/worksheets/${id}`, data),
  updateStatus: (folderId: string, id: string, status: string) =>
    request<{ worksheet: any }>('PATCH', `/folders/${folderId}/worksheets/${id}/status`, { status }),
  delete: (folderId: string, id: string) =>
    request<void>('DELETE', `/folders/${folderId}/worksheets/${id}`),
  duplicate: (folderId: string, id: string) =>
    request<{ worksheet: any }>('POST', `/folders/${folderId}/worksheets/${id}/duplicate`),
  move: (folderId: string, id: string, targetFolderId: string) =>
    request<{ worksheet: any }>('POST', `/folders/${folderId}/worksheets/${id}/move`, { target_folder_id: targetFolderId }),
  import: (folderId: string, useCase: any) =>
    request<{ worksheet: any }>('POST', `/folders/${folderId}/worksheets/import`, { useCase }),
  export: (folderId: string, id: string) =>
    request<{ folder: any; useCase: any; exportedAt: string }>('GET', `/folders/${folderId}/worksheets/${id}/export`),
  copy: (folderId: string, id: string, targetFolderId: string) =>
    request<{ worksheet: any }>('POST', `/folders/${folderId}/worksheets/${id}/copy`, { target_folder_id: targetFolderId }),
  createSymlink: (folderId: string, id: string, targetFolderId: string) =>
    request<{ worksheet: any }>('POST', `/folders/${folderId}/worksheets/${id}/symlink`, { target_folder_id: targetFolderId }),
  updateTags: (folderId: string, id: string, tags: string[]) =>
    request<{ worksheet: any }>('PATCH', `/folders/${folderId}/worksheets/${id}/tags`, { tags }),
  setReadOnly: (folderId: string, id: string, readOnly: boolean) =>
    request<{ worksheet: any }>('PATCH', `/folders/${folderId}/worksheets/${id}/read-only`, { read_only: readOnly }),
  updatePermissions: (folderId: string, id: string, permissions: { user_id: string; role: string }[]) =>
    request<{ worksheet: any }>('PUT', `/folders/${folderId}/worksheets/${id}/permissions`, { permissions }),
};

// ─── Templates ─────────────────────────────────────────

export const templateService = {
  list: () => request<{ templates: any[] }>('GET', '/templates'),
  get: (templateId: string) => request<{ template: any }>('GET', `/templates/${templateId}`),
  getVersions: (templateId: string) =>
    request<{ versions: any[] }>('GET', `/templates/${templateId}/versions`),
  create: (data: { template_id: string; label: string; abbr?: string; description?: string; actors?: string[]; controls?: string[] }) =>
    request<{ template: any }>('POST', '/templates', data),
  publishVersion: (templateId: string, data: { bump: 'major' | 'minor' | 'patch'; migration_notes?: string; actors?: string[]; controls?: string[] }) =>
    request<{ template: any }>('POST', `/templates/${templateId}/versions`, data),
  update: (templateId: string, data: Partial<{ label: string; abbr: string; description: string; actors: string[]; controls: string[] }>) =>
    request<{ template: any }>('PATCH', `/templates/${templateId}`, data),
  delete: (templateId: string) =>
    request<void>('DELETE', `/templates/${templateId}`),
};

// ─── Actor Catalog ─────────────────────────────────────

export const actorCatalogService = {
  list: (includeHidden = false) =>
    request<{ actors: any[] }>('GET', `/actors${includeHidden ? '?include=hidden' : ''}`),
  get: (id: string) => request<{ actor: any }>('GET', `/actors/${id}`),
  create: (data: { actor_id: string; category: string; abbr: string; label: string; description?: string; role?: string; known_vendors?: string[] }) =>
    request<{ actor: any }>('POST', '/actors', data),
  update: (id: string, data: Partial<{ label: string; description: string; abbr: string; known_vendors: string[]; sort_order: number }>) =>
    request<{ actor: any }>('PATCH', `/actors/${id}`, data),
  setStatus: (id: string, status: 'active' | 'hidden' | 'deleted') =>
    request<{ actor: any }>('PATCH', `/actors/${id}/status`, { status }),
};

// ─── Control Catalog ───────────────────────────────────

export const controlCatalogService = {
  list: (includeHidden = false) =>
    request<{ controls: any[] }>('GET', `/controls${includeHidden ? '?include=hidden' : ''}`),
  get: (id: string) => request<{ control: any }>('GET', `/controls/${id}`),
  create: (data: { control_id: string; category: string; label: string; description?: string }) =>
    request<{ control: any }>('POST', '/controls', data),
  update: (id: string, data: Partial<{ label: string; description: string; sort_order: number }>) =>
    request<{ control: any }>('PATCH', `/controls/${id}`, data),
  setStatus: (id: string, status: 'active' | 'hidden' | 'deleted') =>
    request<{ control: any }>('PATCH', `/controls/${id}/status`, { status }),
};

// ─── Users (Platform Admin) ────────────────────────────

export const userService = {
  list: () => request<{ users: any[] }>('GET', '/users'),
  get: (id: string) => request<{ user: any }>('GET', `/users/${id}`),
  updateRole: (id: string, platform_role: 'admin' | 'user') =>
    request<{ user: any }>('PATCH', `/users/${id}`, { platform_role }),
};

// ─── Reports ──────────────────────────────────────────

export const reportService = {
  list: (folderId: string, type?: string) =>
    request<{ reports: any[] }>('GET', `/folders/${folderId}/reports${type ? `?type=${type}` : ''}`),
  get: (folderId: string, reportId: string) =>
    request<{ report: any }>('GET', `/folders/${folderId}/reports/${reportId}`),
  generate: (folderId: string, type?: string) =>
    request<{ reports: any[] }>('POST', `/folders/${folderId}/reports/generate`, type ? { type } : {}),
};
